import React, { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ReactComponent as ShareIcon } from '@pkg/assets/icons/v2/share.svg';
import { MenuItem } from '@pkg/components/MainNavigation/types';
import { getRemainingInvites } from '@pkg/components/Sections/Groups/components/MembershipPanel/components/MembersPanel/components/Footer/utilities';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { Indicator } from '@pkg/components/Sections/Profile/Indicator';
import { Variants } from '@pkg/components/Sections/Profile/Indicator/styles';
import { TwitterPromptModal } from '@pkg/components/TwitterConnect/TwitterPromptModal';
import { useAppDispatch, useAppSelector, useGetInviteData } from '@pkg/hooks';

import { IconButton } from '@app/pages/Prototype/PublishBar/styles';
import { Popover } from '@app/pages/Prototype/components/Profile/components/Popover';
import { PathParams } from '@app/routes/types';
import {
    setMobileShareModalIsOpen,
    setMobileUserNavigationIsOpen,
    setTwitterConnectIsOpen,
} from '@app/store/actions/dashboard';

import { ReactComponent as EditIcon } from '../../assets/icons/v2/edit.svg';
import { LOCAL_STORAGE_EDIT_PROFILE_PROMPT_SEEN } from '../MainNavigation';
import { GiftIcon, HomeIcon, OverflowMenuIcon, SearchIcon, SparklesIcon, UserIcon } from '../MainNavigation/icons';
import { EditButtonWrapper, NotificationDot, TopButtonsMobile } from '../MainNavigation/styles';

import { MobileUserMenu } from './MobileUserMenu';
import { type MainNavigationMobileProps } from './types';

import {
    ButtonsWrapper,
    MainNavigationMobileContainer,
    NavigationButton,
    NavigationButtonIndicator,
    NavigationButtonInside,
} from './styles';

export const MainNavigationMobile = ({ activeItem, onSelectMenuItem, notifyDotProfile }: MainNavigationMobileProps) => {
    const showInviteToYat = useAppSelector((state) => state.featureFlags.features.showInviteToYat);

    const mobileUserNavigationIsOpen = useAppSelector((state) => state.dashboard.mobileUserNavigationIsOpen);
    const dispatch = useAppDispatch();

    const { t } = useTranslation(['yat_page']);

    const primaryYat = useAppSelector((state) => state.user?.primaryYat);
    const { [PathParams.Yat]: yat } = useParams();

    const { data: inviteData } = useGetInviteData();

    const invitesRemaining = getRemainingInvites(inviteData);

    const isCurrentUsersProfile = primaryYat === yat;

    const handleToggleTwitter = useCallback(() => {
        dispatch(setTwitterConnectIsOpen(true));
    }, [dispatch]);

    const openMobileShare = useCallback(() => {
        dispatch(setMobileShareModalIsOpen(true));
    }, [dispatch]);

    const indicator = !!invitesRemaining && (
        <NavigationButtonIndicator>
            <Indicator variant={Variants.secondary} value={invitesRemaining} />
        </NavigationButtonIndicator>
    );

    return (
        <>
            <MainNavigationMobileContainer>
                <ButtonsWrapper>
                    <NavigationButton onClick={() => onSelectMenuItem(MenuItem.Pods)}>
                        <HomeIcon isActive={activeItem === MenuItem.Pods} />
                    </NavigationButton>
                    <NavigationButton onClick={() => onSelectMenuItem(MenuItem.Profile)}>
                        <NavigationButtonInside>
                            <UserIcon isActive={activeItem === MenuItem.Profile} />
                            {notifyDotProfile && !isCurrentUsersProfile && <NotificationDot />}
                        </NavigationButtonInside>
                    </NavigationButton>
                    {showInviteToYat && (
                        <NavigationButton onClick={() => onSelectMenuItem(MenuItem.Invite, invitesRemaining)}>
                            <NavigationButtonInside>
                                <GiftIcon isActive={activeItem === MenuItem.Invite} />
                                {indicator}
                            </NavigationButtonInside>
                        </NavigationButton>
                    )}
                    <NavigationButton onClick={() => onSelectMenuItem(MenuItem.Yativerse)}>
                        <SparklesIcon isActive={activeItem === MenuItem.Yativerse} />
                    </NavigationButton>
                    <NavigationButton onClick={() => onSelectMenuItem(MenuItem.GlobalSearch)}>
                        <SearchIcon isActive={activeItem === MenuItem.GlobalSearch} />
                    </NavigationButton>
                    <NavigationButton
                        aria-controls="yat-dashboard-mobile-user-nav"
                        aria-expanded={mobileUserNavigationIsOpen}
                        onClick={() => dispatch(setMobileUserNavigationIsOpen(true))}
                    >
                        <OverflowMenuIcon />
                    </NavigationButton>
                </ButtonsWrapper>
            </MainNavigationMobileContainer>
            <MobileUserMenu />
            {!isCurrentUsersProfile && (
                <TwitterPromptModal toggleOpen={handleToggleTwitter} isMobile forceClose={mobileUserNavigationIsOpen} />
            )}
            {isCurrentUsersProfile && primaryYat ? (
                <TopButtonsMobile>
                    <EditButtonWrapper>
                        <Button variant="highlight" size="small" startIcon={<EditIcon />} to="/profile/edit">
                            {t('edit_profile')}
                        </Button>
                        <Popover
                            title={t('edit_profile_popover_title')}
                            text={t('edit_profile_popover_text')}
                            localStorageKey={LOCAL_STORAGE_EDIT_PROFILE_PROMPT_SEEN}
                            positionArrow="top-left"
                        />
                    </EditButtonWrapper>

                    <IconButton onClick={openMobileShare} variant="solid" color="transparent" size="small">
                        <ShareIcon />
                    </IconButton>
                </TopButtonsMobile>
            ) : null}
        </>
    );
};
